import { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from '@saas-pages/shared/ui'
import { startToolOperation } from '@saas-pages/shared/public/runtime'
import {
  OpenImageViewerShell,
  type OpenImageViewerActionContext,
  type OpenImageViewerConfig,
} from '@/features/open-image-viewer'
import { useT, type Translations } from '@/shared/i18n'
import { hasAvifFileType } from './avifFile'
import { encodeImage, loadAvifImage, MAX_EXPORT_PIXELS } from './avifImage'

type ExportFormat = 'jpg' | 'png'
type AvifViewerCopy = Translations['avifViewer']

const ANALYTICS_KEY = 'avif-viewer'
const ANALYTICS_CONTEXT = {
  app: 'imgify',
  domain: 'imgify.ru',
  route: '/otkryt-avif',
  utility: ANALYTICS_KEY,
  processingMode: 'local',
} as const

function outputFilename(inputName: string, format: ExportFormat): string {
  const base = inputName.replace(/\.[^.]+$/, '') || 'image'
  return `${base}.${format}`
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

function AvifExportActions({
  context,
  copy,
}: {
  context: OpenImageViewerActionContext
  copy: AvifViewerCopy
}) {
  const [exporting, setExporting] = useState<ExportFormat | null>(null)
  const [error, setError] = useState<string | null>(null)
  const aliveRef = useRef(true)
  const exportGenerationRef = useRef(0)
  const exportAbortRef = useRef<AbortController | null>(null)
  const exportImageElement = context.image.element
  const canExport = Boolean(exportImageElement) &&
    context.image.width * context.image.height <= MAX_EXPORT_PIXELS

  useEffect(() => {
    aliveRef.current = true
    return () => {
      aliveRef.current = false
      exportGenerationRef.current += 1
      exportAbortRef.current?.abort()
      exportAbortRef.current = null
    }
  }, [])

  const exportImage = useCallback(
    async (format: ExportFormat) => {
      if (!canExport) return
      setExporting(format)
      setError(null)
      const generation = ++exportGenerationRef.current
      exportAbortRef.current?.abort()
      const exportAbortController = new AbortController()
      exportAbortRef.current = exportAbortController
      const operation = startToolOperation(ANALYTICS_CONTEXT)
      const startTime = performance.now()

      try {
        if (!exportImageElement) return
        const blob = await encodeImage(exportImageElement, format, copy, exportAbortController.signal)
        if (!aliveRef.current || generation !== exportGenerationRef.current) return
        downloadBlob(blob, outputFilename(context.file.name, format))
        operation.success(Math.round(performance.now() - startTime))
      } catch {
        if (!aliveRef.current || generation !== exportGenerationRef.current) return
        operation.error('processing_failed')
        setError(copy.exportError)
      } finally {
        if (exportAbortRef.current === exportAbortController) exportAbortRef.current = null
        if (aliveRef.current && generation === exportGenerationRef.current) setExporting(null)
      }
    },
    [canExport, context, copy],
  )

  return (
    <div className="space-y-3">
      <p className="text-sm leading-5 text-muted-foreground">
        {copy.exportDescription}
      </p>
      <div className="grid gap-2">
        <Button
          type="button"
          disabled={exporting !== null || !canExport}
          onClick={() => void exportImage('jpg')}
        >
          {exporting === 'jpg' ? copy.exportingJpg : copy.downloadJpg}
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={exporting !== null || !canExport}
          onClick={() => void exportImage('png')}
        >
          {exporting === 'png' ? copy.exportingPng : copy.downloadPng}
        </Button>
      </div>
      {!canExport && <p className="text-sm leading-5 text-destructive">{copy.exportLimit}</p>}
      {error && <p className="text-sm leading-5 text-destructive">{error}</p>}
    </div>
  )
}

export function AvifViewerPage() {
  const t = useT()
  const copy = t.avifViewer
  const config: OpenImageViewerConfig = {
    analyticsContext: ANALYTICS_CONTEXT,
    accept: '.avif,image/avif',
    acceptedMimeTypes: ['image/avif'],
    acceptedExtensions: ['.avif'],
    maxFileSizeBytes: 50 * 1024 * 1024,
    maxPixels: 36_000_000,
    formatLabel: 'AVIF',
    previewBackground: 'checkerboard',
    validateFile: (file, signal) => hasAvifFileType(file, signal),
    loadImage: (file, signal) => loadAvifImage(file, copy, config, signal),
    renderActions: (context) => <AvifExportActions context={context} copy={copy} />,
    convertHref: '/avif-konverter',
    relatedTools: copy.relatedTools,
  }

  return <OpenImageViewerShell copy={copy} config={config} />
}
